import styles from './ProjectHourEvent.module.scss';

import { CloseRounded } from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';
import clsx from 'clsx';
import { Duration } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface ProjectHourEventPrpos {
  fixed: boolean;
  suggested: boolean | null;
  duration?: Duration;
  project?: string;
  customer?: string;
  contract?: string;
  ticket?: string;
  comment?: string;
  activity?: string;
  onHide?: () => void;
  rightElement?: React.ReactNode;
  isStopwatch?: boolean;
}

interface ProjectHourEventSection {
  key: ProjectHourEventSectionType;
  value: string;
}
type ProjectHourEventSectionType =
  | 'project'
  | 'customer'
  | 'contract'
  | 'ticket'
  | 'comment'
  | 'activity';

const ProjectHourEvent = ({
  fixed,
  suggested,
  duration,
  project = '',
  customer = '',
  contract = '',
  ticket = '',
  comment = '',
  activity = '',
  onHide,
  rightElement,
  isStopwatch,
}: ProjectHourEventPrpos) => {
  const content = useMemo(() => {
    let values: ProjectHourEventSection[] = [
      { key: 'project', value: project.trim() },
      { key: 'customer', value: customer.trim() },
      { key: 'contract', value: contract.trim() },
      { key: 'activity', value: activity.trim() },
      { key: 'ticket', value: ticket.trim() },
      { key: 'comment', value: comment.trim() },
    ];
    values = values.filter((el) => el.value);

    if (values.length > 0) {
      return values.map((el, idx) => {
        if (idx === 0) {
          return (
            <FirstRow
              el={el}
              duration={duration}
              onHide={onHide}
              suggested={suggested}
              rightElement={rightElement}
              isStopwatch={isStopwatch}
            />
          );
        }
        return <SecondaryRow el={el} isStopwatch={isStopwatch} />;
      });
    }
    return [
      <div className={styles.firstLine}>
        <div className={styles.row}></div>
        <DurationAndHideIcon
          duration={duration}
          onHide={onHide}
          suggested={suggested}
          isStopwatch={isStopwatch}
        />
        {rightElement}
      </div>,
    ];
  }, [
    project,
    customer,
    contract,
    ticket,
    comment,
    activity,
    duration,
    onHide,
    suggested,
    rightElement,
    isStopwatch,
  ]);

  return (
    <ContentComponent
      suggested={suggested}
      fixed={fixed}
      withRightElement={!!rightElement}
      isStopwatch={isStopwatch}
    >
      {fixed ? content[0] : content}
    </ContentComponent>
  );
};

interface RowProps {
  el: ProjectHourEventSection;
  variant?: 'body1' | 'caption';
  color?: string;
  fontSize?: string;
}

const Row = ({ children }) => {
  return <div className={styles.row}>{children}</div>;
};

const FirstRow = (props: {
  el;
  duration;
  onHide;
  suggested;
  rightElement;
  isStopwatch;
}) => {
  const { el, rightElement } = props;

  const { isStopwatch } = props;
  let component;
  switch (el.key) {
    case 'project':
      component = (
        <ValueRow
          {...props}
          variant="body1"
          color={isStopwatch ? 'var(--brand02)' : 'text.primary'}
        />
      );
      break;
    default:
      component = (
        <>
          <KeyRow
            {...props}
            variant="body1"
            color={isStopwatch ? 'var(--brand02)' : 'text.tertiary'}
          />
          <ValueRow
            {...props}
            variant="body1"
            color={isStopwatch ? 'var(--brand02)' : 'text.primary'}
          />
        </>
      );
      break;
  }

  return (
    <div className={styles.firstLine}>
      <Row>{component}</Row>
      <DurationAndHideIcon {...props} />
      {rightElement}
    </div>
  );
};

const SecondaryRow = (props: { el; isStopwatch }) => {
  const { el } = props;

  const { isStopwatch } = props;
  let component;
  switch (el.key) {
    default:
      component = (
        <div className={styles.secondRowContainer}>
          <KeyRow
            {...props}
            variant="caption"
            color={isStopwatch ? 'var(--brand02)' : 'var(--text03)'}
          />
          <ValueRow
            {...props}
            variant="caption"
            color={isStopwatch ? 'var(--brand02)' : 'var(--text01)'}
          />
        </div>
      );
      break;
  }

  return <Row>{component}</Row>;
};

const KeyRow = (props: RowProps) => {
  const { el, variant, color } = props;
  const { t } = useTranslation();

  return (
    <Typography variant={variant} color={color}>
      {t('COMMON.' + el?.key.toUpperCase())}:&nbsp;
    </Typography>
  );
};

const ValueRow = (props: RowProps) => {
  const { el, variant, color, fontSize } = props;

  return (
    <Typography
      variant={variant}
      color={color}
      fontSize={fontSize}
      className={clsx(
        el?.key !== 'comment' && styles.row,
        el?.key === 'comment' && styles.comment
      )}
    >
      {el?.value}
    </Typography>
  );
};

const DurationAndHideIcon = ({ duration, onHide, suggested, isStopwatch }) => {
  return (
    <div className={styles.durationAndHideIcon}>
      {duration && (
        <Typography
          className={styles.timeBox}
          color={isStopwatch ? 'var(--brand02)' : 'text.tertiary'}
        >
          {duration.toFormat('hh:mm')} h
        </Typography>
      )}

      {suggested && (
        <IconButton
          size="small"
          onClick={(e) => {
            //to prevent opning the mask
            e.stopPropagation();
            onHide();
          }}
        >
          <CloseRounded
            sx={(theme) => ({ color: theme.vars.palette.text.secondary })}
          />
        </IconButton>
      )}
    </div>
  );
};

const ContentComponent = ({
  suggested,
  fixed,
  children,
  withRightElement,
  isStopwatch,
}) => {
  return (
    <div className={styles.container}>
      {suggested && (
        <div className={clsx(styles.addContainer, fixed && styles.fixed)}>
          +
        </div>
      )}
      <div
        className={clsx(
          styles.contentContainer,
          fixed && styles.fixed,
          withRightElement && styles.fixedWithRightElement,
          suggested && styles.suggested,
          isStopwatch && styles.stopwatch
        )}
      >
        {children}
      </div>
    </div>
  );
};

export default ProjectHourEvent;
